/**
 * /giveaways — player-facing list of currently-open giveaways.
 *
 * Backed by GET /api/giveaways. Each card shows:
 *   • Prize name + first item's image
 *   • Time remaining (live-counting), entry count, winner count
 *   • Enter button (or "Entered ✅" pill) gated on:
 *       - logged in
 *       - Discord linked
 *       - giveaway not role-restricted (bot-only)
 *
 * Refreshes the list every 30s + after any successful enter so the
 * counts stay live without needing a websocket.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { Gift, Clock, Users, Lock } from "lucide-react";

function timeRemaining(endIso) {
  if (!endIso) return "ends soon";
  const ms = new Date(endIso).getTime() - Date.now();
  if (ms <= 0) return "ending now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export default function Giveaways() {
  const { user } = useAuth();
  const toast = useToast();
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myDiscordId, setMyDiscordId] = useState(null);
  const [myServers, setMyServers] = useState([]);      // linked servers
  const [busy, setBusy] = useState(null); // message_id currently entering
  const [pickingFor, setPickingFor] = useState(null);  // giveaway needing server pick
  const [tick, setTick] = useState(0);    // forces re-render for live countdown
  // Pending wins from `giveaway_web_deliveries` — populated when the
  // user won a giveaway they entered on the website. Prize sits in
  // 'pending' status until the user claims (picks target server).
  const [pendingWins, setPendingWins] = useState([]);
  const [claiming, setClaiming] = useState(null);

  const load = () => api.get("/giveaways")
    .then((r) => {
      setGiveaways(r.data.giveaways || []);
      setMyDiscordId(r.data.my_discord_id || null);
    })
    .catch((e) => toast.error(apiError(e)))
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // Fetch the user's linked servers so we know whether to show the
    // picker modal or auto-enter. `my-servers` is auth-required — it'll
    // 401 for anon and we just leave `myServers=[]`.
    if (user) {
      api.get("/giveaways/my-servers")
        .then((r) => setMyServers(r.data?.servers || []))
        .catch(() => setMyServers([]));
      // Poll pending wins so the "🎁 Claim your prize" banner appears
      // as soon as a giveaway the user entered on the site resolves.
      const loadWins = () => api.get("/giveaways/my-wins")
        .then((r) => setPendingWins(r.data?.wins || []))
        .catch(() => setPendingWins([]));
      loadWins();
      const winsId = setInterval(loadWins, 30000);
      const id = setInterval(load, 30000);
      const tickId = setInterval(() => setTick((t) => t + 1), 60000);
      return () => { clearInterval(id); clearInterval(tickId); clearInterval(winsId); };
    }
    const id = setInterval(load, 30000);
    const tickId = setInterval(() => setTick((t) => t + 1), 60000);
    return () => { clearInterval(id); clearInterval(tickId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const claimWin = async (win, serverName) => {
    setClaiming(win.delivery_id);
    try {
      const { data } = await api.post(`/giveaways/wins/${win.delivery_id}/claim`, null, {
        params: { server_name: serverName },
      });
      // Backend now attempts INSTANT auto-delivery via SFTP + RCON. If
      // every prize made it in-game we show a confetti-style success;
      // otherwise we surface the per-prize outcome so the user knows
      // exactly what landed vs. what needs admin follow-up.
      if (data.delivered) {
        toast.success(`🎉 Prize delivered to your in-game character on ${serverName}!`);
      } else {
        const ok = (data.results || []).filter((r) => r.ok).length;
        const total = (data.results || []).length;
        if (ok > 0 && ok < total) {
          toast.warning(`${ok}/${total} prizes delivered — admin will fulfil the rest.`);
        } else {
          toast.error(
            `Claim saved but auto-delivery didn't complete — an admin will push the prize shortly.`
          );
        }
      }
      setPendingWins((prev) => prev.filter((w) => w.delivery_id !== win.delivery_id));
    } catch (e) {
      toast.error(apiError(e));
    } finally { setClaiming(null); }
  };

  const enter = async (g, serverName = null) => {
    // Client-side server picker gate. If the user is linked to more
    // than one server, force them to choose (matches the Discord bot's
    // GiveawayServerSelectView flow). If exactly one, auto-pick so the
    // click-to-enter experience stays snappy.
    if (!serverName) {
      if (myServers.length > 1) {
        setPickingFor(g);
        return;
      }
      serverName = myServers[0]?.server_name || null;
    }
    setBusy(g.message_id);
    setPickingFor(null);
    try {
      const params = serverName ? { server_name: serverName } : {};
      const { data } = await api.post(`/giveaways/${g.message_id}/enter`, null, { params });
      const target = data.server_name ? ` — prizes → ${data.server_name}` : "";
      toast.success(data.already_entered ? "You're already entered" : `Entered! ${data.entry_count} entrants 🎟️${target}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(null); }
  };

  if (loading) return <Loader label="Loading giveaways…" />;

  return (
    <div data-testid="giveaways-page">
      <PageHeader
        title="Giveaways"
        subtitle="Enter directly from the portal — winners are picked automatically."
      />

      {!user && (
        <div className="glass-card p-6 mb-6 text-center text-sm" data-testid="giveaways-login-prompt">
          <Lock className="mx-auto text-[var(--muted)] mb-2" size={20} />
          <Link to="/login" className="text-[var(--teal)] glow-teal">Log in</Link>{" "}
          <span className="text-[var(--muted)]">to enter giveaways from the portal.</span>
        </div>
      )}

      {user && myServers.length === 0 && (
        <div className="glass-card p-4 mb-6 border-yellow-400/40 text-sm" style={{ borderColor: "rgba(250, 204, 21, 0.4)" }} data-testid="giveaways-link-character-prompt">
          <span className="text-yellow-200 font-semibold">⚠️ Link an in-game character</span>
          <p className="text-[var(--muted)] text-xs mt-1">
            You need at least one linked character so we know where to
            deliver your prize. Head to the{" "}
            <Link to="/link" className="text-[var(--teal)] underline">Link Account</Link>{" "}
            page — no Discord required.
          </p>
        </div>
      )}

      {/* Pending wins banner — appears when a giveaway the user
          entered on the website has ended AND they were picked as a
          winner but haven't claimed yet. Each win shows the prize
          bundle + a server picker so they can direct the delivery. */}
      {pendingWins.length > 0 && (
        <div
          className="glass-card p-5 border border-[var(--gold)] bg-gradient-to-br from-[var(--gold)]/10 to-transparent"
          data-testid="pending-wins-banner"
        >
          <h2 className="text-lg mb-2 flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            🎁 You won! Claim {pendingWins.length} prize{pendingWins.length > 1 ? "s" : ""}
          </h2>
          <p className="text-xs text-[var(--muted)] mb-3">
            Pick your delivery server and we&apos;ll queue the drop. An
            admin will confirm and push it to your linked in-game
            character shortly after claim.
          </p>
          <ul className="space-y-2">
            {pendingWins.map((w) => (
              <li key={w.delivery_id} className="bg-black/30 rounded p-3 space-y-2" data-testid={`pending-win-${w.delivery_id}`}>
                <div className="text-sm">
                  <span className="font-mono text-[var(--gold)]">#{w.delivery_id}</span> —{" "}
                  {(w.prize_bundle || []).map((p) => p.display).join(", ") || "prize bundle"}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {myServers.length > 0 ? (
                    myServers.map((s) => (
                      <button
                        key={s.server_name}
                        onClick={() => claimWin(w, s.server_name)}
                        disabled={claiming === w.delivery_id}
                        className="btn btn-primary text-xs"
                        data-testid={`claim-win-${w.delivery_id}-${s.server_name.replace(/\W+/g, "_")}`}
                      >
                        ✅ Claim to {s.server_name.replace(/Chillet's /, "")}
                      </button>
                    ))
                  ) : (
                    <span className="text-[10px] text-[var(--danger)]">
                      Link your in-game character first (<Link to="/link" className="underline">Link Account page</Link>) then refresh.
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {giveaways.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="giveaways-empty">
          <Gift className="mx-auto text-[var(--muted)] mb-3" size={36} />
          <h3 className="text-lg mb-1">No active giveaways</h3>
          <p className="text-xs text-[var(--muted)]">Check back soon — admins post new prizes regularly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="giveaways-list">
          {giveaways.map((g) => (
            <GiveawayCard
              key={g.message_id}
              giveaway={g}
              myServers={myServers}
              user={user}
              onEnter={() => enter(g)}
              entering={busy === g.message_id}
              tick={tick}
            />
          ))}
        </div>
      )}

      {/* Server-picker modal — shown when the user is linked to more
          than one game server. Mirrors the Discord bot's
          `GiveawayServerSelectView` so the entry experience is
          consistent across surfaces. */}
      {pickingFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPickingFor(null)}
          data-testid="giveaway-server-picker-backdrop"
        >
          <div
            className="glass-card p-6 max-w-md w-full space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="giveaway-server-picker"
          >
            <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              Choose your server
            </h3>
            <p className="text-xs text-[var(--muted)]">
              Which server should your prize be delivered to if you win?
              Pick the one you play on.
            </p>
            <div className="space-y-2">
              {myServers.map((s) => (
                <button
                  key={s.server_name}
                  onClick={() => enter(pickingFor, s.server_name)}
                  disabled={busy === pickingFor.message_id}
                  className="w-full text-left glass-card p-3 bg-black/30 hover:bg-white/5 transition-colors"
                  data-testid={`giveaway-server-pick-${s.server_name.replace(/\W+/g, "_")}`}
                >
                  <div className="text-sm text-[var(--bone)]">{s.server_name}</div>
                  <div className="text-[10px] text-[var(--muted)] font-mono truncate">
                    Linked as {s.player_name}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setPickingFor(null)}
              className="btn btn-secondary w-full"
              data-testid="giveaway-server-pick-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GiveawayCard({ giveaway: g, myServers, user, onEnter, entering, tick }) {
  // First image in the prize bundle, falling back to the legacy field.
  const heroImg = useMemo(() => {
    const fromBundle = (g.prize_bundle || []).find((p) => p.image_url)?.image_url;
    return fromBundle || g.item_image_url || null;
  }, [g.prize_bundle, g.item_image_url]);

  const remaining = timeRemaining(g.end_time);
  const hasLinkedCharacter = (myServers?.length || 0) > 0;
  const canEnter = user && hasLinkedCharacter && !g.entered;
  const slug = g.message_id.replace(/[^a-z0-9]/gi, "");

  return (
    <div
      className={`glass-card p-5 fade-up flex flex-col ${g.entered ? "border-emerald-400/40" : ""}`}
      data-testid={`giveaway-card-${slug}`}
      style={g.entered ? { borderColor: "rgba(52, 211, 153, 0.4)" } : undefined}
    >
      <div className="flex items-start gap-3">
        {heroImg && (
          <img
            src={heroImg}
            alt={g.prize}
            className="w-14 h-14 rounded object-cover bg-black/40 shrink-0"
            loading="lazy"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm text-[var(--bone)]">{g.prize}</div>
          <div className="text-[10px] text-[var(--muted)] mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock size={10} /> {remaining}{tick > -1 ? "" : ""}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={10} /> {g.entry_count.toLocaleString()} entered
            </span>
            <span>
              {g.winners} winner{g.winners === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {g.prize_bundle && g.prize_bundle.length > 1 && (
        <ul className="mt-3 space-y-1 text-[10px] text-[var(--muted)]">
          {g.prize_bundle.slice(0, 5).map((p, i) => (
            <li key={i} className="flex items-center gap-1.5 truncate">
              <span className="w-1 h-1 rounded-full bg-[var(--teal)]"></span>
              <span className="truncate">{p.display}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex-1" />
      <div className="mt-4 flex items-center gap-2">
        {g.entered ? (
          <div
            className="flex-1 text-center py-2 px-3 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 text-sm uppercase tracking-wider"
            data-testid={`giveaway-card-${slug}-entered-pill`}
          >
            ✅ Entered
          </div>
        ) : (
          <button
            onClick={onEnter}
            disabled={!canEnter || entering}
            className="btn btn-primary flex-1"
            data-testid={`giveaway-card-${slug}-enter-btn`}
          >
            {entering ? "Entering…" : !user ? "Log in to enter" : !hasLinkedCharacter ? "Link a character to enter" : "Enter Giveaway"}
          </button>
        )}
      </div>
    </div>
  );
}
